import {useDebouncedValue} from '@mantine/hooks';
import {IconSearch} from '@tabler/icons-react';
import React, {useCallback, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {InspectionScheduleDrawer} from 'src/app/inspections/schedules/InspectionScheduleDrawer';
import {useInspectionScheduleColumns} from 'src/app/inspections/schedules/useInspectionScheduleColumns';
import {useInspectionScheduleCrud} from 'src/app/inspections/schedules/useInspectionScheduleCrud';
import {useScheduleBulkActive} from 'src/app/inspections/schedules/useScheduleBulkActive';
import {Button} from 'src/design/atoms/Button';
import {Textbox} from 'src/design/atoms/Textbox';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {useGetInspectionScheduleListV2Query} from 'src/redux/queries/inspectionSchedule';
import {useInspectionPermissions} from 'src/shared/auth/permissions/useInspectionPermissions';

export const InspectionSchedulesTab: React.FC = () => {
  const perms = useInspectionPermissions();
  const canManage = perms.manageSchedules;

  const [searchParams, setSearchParams] = useSearchParams();
  const scheduleParam = Number(searchParams.get('schedule'));
  const drawerScheduleId = Number.isFinite(scheduleParam) && scheduleParam > 0
    ? scheduleParam
    : undefined;

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchInput, 300);

  const crud = useInspectionScheduleCrud({canManage});

  const openDrawer = useCallback(
    (id: number) => {
      setSearchParams(previous => {
        const next = new URLSearchParams(previous);
        next.set('schedule', String(id));
        return next;
      });
    },
    [setSearchParams]
  );

  const closeDrawer = useCallback(() => {
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      next.delete('schedule');
      return next;
    });
  }, [setSearchParams]);

  const columns = useInspectionScheduleColumns({includeSite: true});

  const tableState = useTableState({
    identifier: 'inspection-schedules-v1',
    columns,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 20},
    defaultSorting: [{id: 'nextDueAt', desc: false}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetInspectionScheduleListV2Query({
      start,
      length,
      filters,
      filterFns,
      sorting,
      search: debouncedSearch || undefined,
    });
  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;
  const hasActiveFilters = filters.length > 0 || !!debouncedSearch;

  const bulkActive = useScheduleBulkActive({rows, canManage});

  const {table} = useTable({
    ...tableState,
    rowSelection: bulkActive.rowSelection,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      enableRowSelection: bulkActive.enableRowSelection,
      columns,
      data: rows,
      getRowId: row => String(row.id),
      onRowSelectionChange: bulkActive.setRowSelection,
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View onClick={() => openDrawer(row.original.id)} />
          {canManage && (
            <TableRowActions.Edit onClick={() => crud.openEdit(row.original)} />
          )}
          {canManage && (
            <TableRowActions.Menu>
              <TableRowActions.MenuItem
                color="red"
                onClick={() => crud.requestDelete(row.original)}>
                Delete
              </TableRowActions.MenuItem>
            </TableRowActions.Menu>
          )}
        </TableRowActions>
      ),
      renderEmptyRowsFallback: () =>
        hasActiveFilters ? (
          <NoItemsPanel noItemsText="No recurrence schedules match these filters." />
        ) : (
          <NoItemsPanel
            heading="No recurrence schedules yet"
            noItemsText="A schedule generates an inspection for a site on a repeating cadence, ahead of its due date."
            canCreate={canManage}
            createNode={
              canManage ? (
                <Button onClick={crud.openCreate}>Add schedule</Button>
              ) : undefined
            }
          />
        ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Filters>
            <Textbox
              ariaLabel="Search schedules"
              placeholder="Search site or type"
              value={searchInput}
              onChangeCallback={e => setSearchInput(e.target.value)}
              props={{leftSection: <IconSearch size={16} />, w: 240}}
            />
          </TableToolbar.Filters>
          <TableToolbar.Actions>
            {canManage && (
              <TableToolbar.Action variant="create" onClick={crud.openCreate}>
                Add schedule
              </TableToolbar.Action>
            )}
            {bulkActive.actions}
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <>
      {table}

      <InspectionScheduleDrawer
        opened={drawerScheduleId !== undefined}
        onClose={closeDrawer}
        scheduleId={drawerScheduleId}
      />

      {bulkActive.confirmDialog}
      {crud.dialogs}
    </>
  );
};
